import { Modal, Box, InputAdornment, IconButton } from "@mui/material"
import { WhiteTextField } from "common/TextField"
import { RaRecord, useNotify, useDataProvider, useRefresh } from "ra-core"
import React, { useState } from "react"
import { ModalProps } from "./common"
import { Chipify } from "common/Chipify"
import RoundedPanel, { Alert } from "common/RoundedPanel"
import { SumbitButtons } from "common/SubmitButtons"
import { PricingType, Tablet } from "api/company"

/** Wait for portal-api to repoint tablets to the new pricing rule id (async via RabbitMQ). */
const PRICING_RULE_TABLET_REPOINT_DELAY_MS = 2000

interface EditPricingRuleModalProps {
  getTablet?: (type: PricingType) => Tablet | undefined
}

interface AssetPriceInputProps {
  id: string
  label: string
  value: number
  onChange: (value: number) => void
  currency: string
}

const AssetPriceEditInput = ({ id, label, value, onChange, currency }: AssetPriceInputProps) => {

  return (
    <>
      <WhiteTextField
        id={id}
        label={label}
        fullWidth
        onChange={(e) => {
          onChange(parseFloat(e.target.value))
        }}
        defaultValue={value}
        type="number"
        InputProps={{
          endAdornment: <InputAdornment position="start">{currency}</InputAdornment>
        }}
        inputProps={{
          step: 0.0001,
          min: 0,
          max: 99
        }}
      />
      <Box height={16} />
    </>
  )
}

export const EditPricingRuleModal = ({
  open,
  onClose,
  record,
  getTablet
}: ModalProps<RaRecord> & EditPricingRuleModalProps) => {
  const {
    id,
    price,
    companyId,
    stages,
    currency: recordCurrency,
    pricePerWordNewAsset: recordNewAsset,
    pricePerWordRepeatedAsset: recordRepeatedAsset,
    pricePerWordRecycledAsset: recordRecycledAsset,
    type: recordType
  } = record

  const [deleteState, setDeleteState] = useState<"NONE" | "WARN" | "TABLET_EXISTS">(
    (getTablet?.(recordType) && "TABLET_EXISTS") || "NONE"
  )
  const currency = recordCurrency || "EUR"
  const [pricePerWordNewAsset, setPricePerWordNewAsset] = useState<number>(recordNewAsset || price || 0)
  const [pricePerWordRepeatedAsset, setPricePerWordRepeatedAsset] = useState<number>(recordRepeatedAsset || price || 0)
  const [pricePerWordRecycledAsset, setPricePerWordRecycledAsset] = useState<number>(recordRecycledAsset || price || 0)
  const [isSaving, setIsSaving] = useState(false)

  const notify = useNotify()
  const refresh = useRefresh()
  const dataProvider = useDataProvider()

  const applyChanges = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const data: any = {
      price: pricePerWordNewAsset,
      stages: stages,
      companyId: companyId,
      type: recordType,
      pricingIdToDelete: id,
      currency: currency
    }

    if (recordType === "FIXED_ASSET_BASED") {
      data.currency = currency
      data.pricePerWordNewAsset = pricePerWordNewAsset
      data.pricePerWordRepeatedAsset = pricePerWordRepeatedAsset
      data.pricePerWordRecycledAsset = pricePerWordRecycledAsset
    }

    setIsSaving(true)
    dataProvider
      .create(`pricingrules`, { data })
      .then(
        () =>
          new Promise<void>((resolve) => {
            setTimeout(resolve, PRICING_RULE_TABLET_REPOINT_DELAY_MS)
          })
      )
      .then(() => {
        notify("Price rule was successfully updated.", { type: "success" })
        refresh()
        onClose()
      })
      .catch((error: any) => {
        refresh()
        notify(error?.message ?? "Something went wrong.", { type: "error" })
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const onDelete = () => {
    if (deleteState == "NONE") {
      setDeleteState("WARN")
    }
    if (deleteState == "WARN") {
      dataProvider
        .delete("pricingrules", { id: id, previousData: record })
        .then(() => {
          notify("Price rule was successfully deleted.", { type: "success" })
          onClose()
        })
        .catch((error) => {
          notify(error?.message ?? "Something went wrong.", { type: "error" })
        })
        .finally(() => {
          refresh()
        })
    }
  }

  const deleteWarn =
    deleteState == "WARN" ? (
      <Box margin="20px 0px">
        <Alert variant="warn">Are you sure you wanto to delete this item?</Alert>
      </Box>
    ) : (
      <Box height={32} />
    )

  return (
    <Modal open={open} onClose={onClose} disableAutoFocus={true}>
      <RoundedPanel modal width="400px">
        <form onSubmit={applyChanges}>
          <h1>
            Workflow: <Chipify source={stages} translation="stage.type." variant="squere-chip" exclude={["FILE_PREPARATION", "FILE_PARSING", "RECYCLING_ONLY"]}/>
          </h1>
          {recordType === "FIXED_ASSET_BASED" ? (
            <>
              <Box height={16} />
              <AssetPriceEditInput
                id="pricePerWordNewAsset"
                label="Price per word (New Asset)"
                value={pricePerWordNewAsset}
                onChange={setPricePerWordNewAsset}
                currency={currency}
              />
              <AssetPriceEditInput
                id="pricePerWordRepeatedAsset"
                label="Price per word (Repeated Asset)"
                value={pricePerWordRepeatedAsset}
                onChange={setPricePerWordRepeatedAsset}
                currency={currency}
              />
              <AssetPriceEditInput
                id="pricePerWordRecycledAsset"
                label="Price per word (Recycled Asset)"
                value={pricePerWordRecycledAsset}
                onChange={setPricePerWordRecycledAsset}
                currency={currency}
              />
            </>
          ) : recordType === "FIXED" ? (
            <>
              <Box height={16} />
              <AssetPriceEditInput
                id="pricePerWordNewAsset"
                label="Price per word"
                value={pricePerWordNewAsset}
                onChange={setPricePerWordNewAsset}
                currency={currency}
              />
            </>
          ) : (
            <WhiteTextField
              id="pricePerWord"
              label="Price per word"
              fullWidth
              onChange={(e) => {
                setPricePerWordNewAsset(parseFloat(e.target.value))
              }}
              defaultValue={price}
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="start">{currency}</InputAdornment>
              }}
              inputProps={{
                step: 0.0001,
                min: 0,
                max: 99
              }}
            />
          )}
          {deleteWarn}
          <SumbitButtons
            onClose={onClose}
            onDelete={stages.length > 0 ? onDelete : undefined}
            isLoading={isSaving}
            onDeleteLabel="Delete pricing"
            onDeleteDisabled={deleteState == "TABLET_EXISTS"}
            onDeleteDisabledMessage={deleteState == "TABLET_EXISTS" ? "Please delete Tablet first" : undefined}
          />
        </form>
      </RoundedPanel>
    </Modal>
  )
}
